import { createContext, useContext, useState, ReactNode } from "react";

export interface Product {
  id: string;
  name: string;
  price: number;
  originalPrice: number;
  image: string;
  category: string;
  material: string;
  weight: string;
  purity: string;
  description: string;
  details: string[];
}

export interface CartItem {
  product: Product;
  quantity: number;
}

interface AppContextType {
  isLoggedIn: boolean;
  login: () => void;
  logout: () => void;
  cart: CartItem[];
  addToCart: (product: Product) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, qty: number) => void;
  cartCount: number;
  cartTotal: number;
  clearCart: () => void;
}

const AppContext = createContext<AppContextType | null>(null);

export const DUMMY_PRODUCTS: Product[] = [
  {
    id: "silver-band-ring",
    name: "Classic Silver Band Ring",
    price: 499,
    originalPrice: 649,
    image:
      "https://images.unsplash.com/photo-1605100804763-247f67b3557e?w=400&h=400&fit=crop",
    category: "Rings",
    material: "Silver",
    weight: "3.5g",
    purity: "German Silver",
    description:
      "A timeless minimalist band ring handcrafted from German silver. Smooth polished surface with a comfortable dome fit — an everyday essential for every finger.",
    details: [
      "German Silver — quality verified",
      "Width: 4 mm | Dome finish",
      "Available in sizes 5–13",
      "Anti-tarnish coating",
      "Nickel-free",
      "Comes with a gift box",
    ],
  },
  {
    id: "silver-twisted-rope-ring",
    name: "Silver Twisted Rope Ring",
    price: 749,
    originalPrice: 999,
    image:
      "https://images.unsplash.com/photo-1611591437281-460bfbe1220a?w=400&h=400&fit=crop",
    category: "Rings",
    material: "Silver",
    weight: "5.0g",
    purity: "German Silver",
    description:
      "A bold rope-twist ring artisan-crafted from German silver. Each ring is hand-twisted by skilled silversmiths for a textured, sculptural look that stands out.",
    details: [
      "German Silver — quality verified",
      "Width: 5 mm | Hand-twisted texture",
      "Available in sizes 5–13",
      "Anti-tarnish coating",
      "Nickel-free",
      "Comes with a gift box",
    ],
  },
  {
    id: "silver-solitaire-ring",
    name: "Silver Solitaire Ring",
    price: 999,
    originalPrice: 1299,
    image:
      "https://images.unsplash.com/photo-1603561591411-07134e71a2a9?w=400&h=400&fit=crop",
    category: "Rings",
    material: "Silver",
    weight: "6.2g",
    purity: "German Silver",
    description:
      "An elegant solitaire-style silver ring featuring a single polished dome centerpiece set on a tapered band. Crafted from German silver and priced at current market rates. Perfect for engagements, anniversaries, or everyday elegance.",
    details: [
      "German Silver — quality verified",
      "Solitaire dome top | Tapered band",
      "Available in sizes 5–13",
      "Anti-tarnish coating",
      "Nickel-free",
      "Presented in a premium silver gift box",
    ],
  },
];

export function AppProvider({ children }: { children: ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => localStorage.getItem("app_logged_in") === "true",
  );
  const [cart, setCart] = useState<CartItem[]>([]);

  const login = () => {
    setIsLoggedIn(true);
    localStorage.setItem("app_logged_in", "true");
  };
  const logout = () => {
    setIsLoggedIn(false);
    localStorage.removeItem("app_logged_in");
    setCart([]);
  };

  const addToCart = (product: Product) => {
    setCart((prev) => {
      const existing = prev.find((item) => item.product.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.product.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item,
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const removeFromCart = (productId: string) => {
    setCart((prev) => prev.filter((item) => item.product.id !== productId));
  };

  const updateQuantity = (productId: string, qty: number) => {
    if (qty <= 0) {
      removeFromCart(productId);
      return;
    }
    setCart((prev) =>
      prev.map((item) =>
        item.product.id === productId ? { ...item, quantity: qty } : item,
      ),
    );
  };

  const clearCart = () => setCart([]);

  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const cartTotal = cart.reduce(
    (sum, item) => sum + item.product.price * item.quantity,
    0,
  );

  return (
    <AppContext.Provider
      value={{
        isLoggedIn,
        login,
        logout,
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        cartCount,
        cartTotal,
        clearCart,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppProvider");
  return ctx;
}
